import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { debounce } from 'throttle-debounce';
import { rtlEnable } from '@/domain/env';

import {
  Modal,
  ModalCloseButton,
  PreviewCounter,
  PreviewContent,
  PreviewNavigation,
  PreviewNavigationButton,
} from '@/pages/dokkaPay/pages/company/components/PaymentPreview/components/StyledComponents';
import PreviewActionGroup from '@/pages/dokkaPay/pages/company/components/PaymentPreview/components/PreviewActionGroup';
import CircularProgressWithBackdrop from '@/components/mui/CircularProgressWithBackdrop';
import RouterLink from '@/components/mui/Router/RouterLink';
import Box from '@mui/material/Box';

import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import NavigateNextOutlinedIcon from '@mui/icons-material/NavigateNextOutlined';
import NavigateBeforeOutlinedIcon from '@mui/icons-material/NavigateBeforeOutlined';

interface Size {
  width: number;
  height: number;
}

const scaleSize = (rawSize: Size, ratio: number): Size => {
  return { width: rawSize.width * ratio, height: rawSize.height * ratio };
};

interface PaymentPreviewProps {
  close: () => void;
  src: string | null;
  rotationAngle: number;
  to: string;
  onClick?: () => void;
  onPrev?: (() => void) | false | null;
  onNext?: (() => void) | false | null;
  isDisablePrev?: boolean;
  isDisableNext?: boolean;
  onContextMenu?: ((e: React.MouseEvent) => void) | false | null;
  isContextMenuOpen?: boolean;
  allPagesCount?: number | null;
  currentPage?: number;
  isLoading?: boolean;
  xGap?: number;
}

const PaymentPreview: React.FC<PaymentPreviewProps> = ({
  close,
  src,
  rotationAngle,
  to,
  onClick,
  onPrev,
  onNext,
  isDisablePrev,
  isDisableNext,
  onContextMenu,
  isContextMenuOpen,
  allPagesCount,
  currentPage,
  isLoading,
  xGap = 0,
}) => {
  const imageRulerElRef = useRef<HTMLImageElement>(null);
  const btnBoxElRef = useRef<HTMLButtonElement>(null);
  const [lastLoadedSrc, setLastLoadedSrc] = useState<string | null>(null);
  const [rawSize, setRawSize] = useState({ width: 1, height: 1 });
  const [isSuppressSpinner, setIsSuppressSpinner] = useState(false);
  const loadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [rotationAngleState, setRotationAngleState] = useState(0);

  const isRtl = useSelector(rtlEnable);

  const clearTimer = () => {
    if (loadTimerRef.current) {
      clearTimeout(loadTimerRef.current);
      loadTimerRef.current = null;
    }
  };

  const onInit = useMemo(
    () =>
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (debounce as any)(30, () => {
        setLastLoadedSrc(src);
        const rect = imageRulerElRef.current?.getBoundingClientRect();
        if (rect) {
          setRawSize({ width: rect.width || 1, height: rect.height || 1 });
        }
        setIsSuppressSpinner(false);
        clearTimer();
        setRotationAngleState(rotationAngle);
      }) as () => void,
    [src, rotationAngle, setRotationAngleState],
  );

  useEffect(() => {
    window.addEventListener('resize', onInit);
    return () => {
      clearTimer();
      window.removeEventListener('resize', onInit);
      close();
    };
  }, []);

  useEffect(() => {
    onInit();
  }, [src]);

  const onClickContextBtn = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onContextMenu && !isContextMenuOpen) {
      const rect = btnBoxElRef.current!.getBoundingClientRect();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const mutableEvent = e as any;
      mutableEvent.clientX = rect.x + (isRtl ? rect.width : 0);
      mutableEvent.clientY = rect.y + rect.height + 5;
      onContextMenu(mutableEvent);
    }
  };

  const getAvailableSpace = () => {
    return {
      height: window.innerHeight - 100,
      width: window.innerWidth - xGap - 300,
    };
  };

  const getPresentationImgSize = () => {
    const { width: w, height: h } = rawSize;
    const isShuffle = rotationAngleState ? (rotationAngleState / 90) % 2 : false;
    const [width, height] = isShuffle ? [h, w] : [w, h];
    return { width, height };
  };

  const getRatio = () => {
    const raw = getPresentationImgSize();
    const space = getAvailableSpace();
    const safeWidth = raw.width > 0 ? raw.width : 1;
    const safeHeight = raw.height > 0 ? raw.height : 1;
    return Math.min(space.width / safeWidth, space.height / safeHeight);
  };

  const getBoxStyle = () => {
    return scaleSize(getPresentationImgSize(), getRatio());
  };

  const getImageStyle = () => {
    return scaleSize(rawSize, getRatio());
  };

  const isBusy = !isSuppressSpinner && (src !== lastLoadedSrc || isLoading);

  const renderImage = () => {
    const image = (
      <img src={lastLoadedSrc ?? undefined} alt="payment preview" draggable={false} style={getImageStyle()} />
    );
    if (isContextMenuOpen) {
      return <Box display="flex">{image}</Box>;
    }
    return (
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      <Box display="flex" component={RouterLink as any} href={to} onClick={onClick}>
        {image}
      </Box>
    );
  };

  return (
    <Modal open onClose={close} offset={xGap}>
      <>
        <Box zIndex={-1} position="absolute" height={0} width={0} overflow="hidden">
          <img
            src={src ?? undefined}
            alt="payment preview"
            onLoad={onInit as React.ReactEventHandler<HTMLImageElement>}
            ref={imageRulerElRef}
          />
        </Box>

        {isBusy && <CircularProgressWithBackdrop />}

        {onPrev && (
          <PreviewNavigation>
            <PreviewNavigationButton onClick={onPrev || undefined} disabled={isDisablePrev}>
              {isRtl ? <NavigateNextOutlinedIcon /> : <NavigateBeforeOutlinedIcon />}
            </PreviewNavigationButton>
          </PreviewNavigation>
        )}

        <PreviewContent style={{ ...getBoxStyle() }}>
          <ModalCloseButton onClick={close}>
            <CloseOutlinedIcon />
          </ModalCloseButton>

          <Box boxShadow={10} sx={{ transform: `rotate(${rotationAngleState}deg)` }}>
            {renderImage()}
          </Box>

          {allPagesCount && <PreviewCounter>{`${currentPage} / ${allPagesCount}`}</PreviewCounter>}

          <PreviewActionGroup
            href={to}
            onClickLink={onClick}
            onClickContextMenu={onClickContextBtn}
            withContext={!!onContextMenu}
            isContextMenuOpen={!!isContextMenuOpen}
            contextMenuRef={btnBoxElRef}
          />
        </PreviewContent>

        {onNext && (
          <PreviewNavigation>
            <PreviewNavigationButton onClick={onNext || undefined} disabled={isDisableNext}>
              {isRtl ? <NavigateBeforeOutlinedIcon /> : <NavigateNextOutlinedIcon />}
            </PreviewNavigationButton>
          </PreviewNavigation>
        )}
      </>
    </Modal>
  );
};

export default PaymentPreview;
